import React from 'react';

type TracesCountsContextValue = {
  allLoaded: boolean;
  markLoaded: (id: string) => void;
  registerId: (id: string) => void;
  unregisterId: (id: string) => void;
};

const TracesCountsContext = React.createContext<
  TracesCountsContextValue | undefined
>(undefined);

type TracesCountsProviderProps = {children: React.ReactNode};

export const TracesCountsProvider: React.FC<TracesCountsProviderProps> = ({
  children,
}) => {
  const [pendingIds, setPendingIds] = React.useState<Set<string>>(
    () => new Set()
  );
  const hasAnyRef = React.useRef(false);

  const markLoaded = React.useCallback((id: string) => {
    setPendingIds(prev => {
      if (!prev.has(id)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const registerId = React.useCallback((id: string) => {
    setPendingIds(prev => {
      if (prev.has(id)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    hasAnyRef.current = true;
  }, []);

  const unregisterId = React.useCallback((id: string) => {
    setPendingIds(prev => {
      if (!prev.has(id)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const allLoaded = hasAnyRef.current && pendingIds.size === 0;

  const value: TracesCountsContextValue = React.useMemo(() => {
    return {
      allLoaded,
      markLoaded,
      registerId,
      unregisterId,
    };
  }, [allLoaded, markLoaded, registerId, unregisterId]);

  return (
    <TracesCountsContext.Provider value={value}>
      {children}
    </TracesCountsContext.Provider>
  );
};

export const useTracesCountsContext = (): TracesCountsContextValue => {
  const ctx = React.useContext(TracesCountsContext);
  if (!ctx) {
    throw new Error(
      'useTracesCountsContext must be used within TracesCountsProvider'
    );
  }
  return ctx;
};
